import React, { useState, useEffect } from 'react';
import {
  Activity,
  AlertTriangle,
  CheckCircle,
  Clock,
  Database,
  ExternalLink,
  RefreshCw,
  Shield,
  TrendingUp,
  Users,
  XCircle
} from 'lucide-react';
import { structuredLogger } from '../services/structuredLogger';
import { grafanaMetricsService } from '../services/grafanaMetricsService';
import authService from '../services/authService';

interface AuditLogEntry {
  id: string;
  logged_at: string;
  user_email: string;
  event_type: string;
  severity: string;
  app_version?: string;
  context?: string;
  metadata?: any;
}

interface AnomalyData {
  user_id: string;
  user_email: string;
  anomaly_type: string;
  event_count: number;
  last_occurrence: string;
}

const MonitoringDashboard: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [recentLogs, setRecentLogs] = useState<AuditLogEntry[]>([]);
  const [criticalEvents, setCriticalEvents] = useState<AuditLogEntry[]>([]);
  const [anomalies, setAnomalies] = useState<AnomalyData[]>([]);
  const [metricsStats, setMetricsStats] = useState<any>(null);
  const [grafanaTestResult, setGrafanaTestResult] = useState<string | null>(null);
  const [isTestingGrafana, setIsTestingGrafana] = useState(false);

  const session = authService.getCurrentSession();
  const isSuperAdmin = session?.user?.role === 'superadmin';

  useEffect(() => {
    if (isSuperAdmin) {
      loadMonitoringData();
    }
  }, [isSuperAdmin]);

  const loadMonitoringData = async () => {
    setIsLoading(true);
    try {
      const userId = session?.user?.id;
      if (userId) {
        const [logs, critical, anomalyData, stats] = await Promise.all([
          structuredLogger.getRecentLogs(userId, 50),
          structuredLogger.getCriticalEvents(100),
          structuredLogger.detectAnomalies(24),
          grafanaMetricsService.getMetricsStats()
        ]);

        setRecentLogs(logs);
        setCriticalEvents(critical);
        setAnomalies(anomalyData);
        setMetricsStats(stats);
      }
    } catch (error) {
      console.error('Error loading monitoring data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestGrafana = async () => {
    setIsTestingGrafana(true);
    setGrafanaTestResult(null);
    try {
      const result = await grafanaMetricsService.testConnection();
      setGrafanaTestResult(result.success ? 'success' : result.message);
    } catch (error) {
      setGrafanaTestResult('Error al probar conexión');
    } finally {
      setIsTestingGrafana(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50';
      case 'error': return 'text-red-500 bg-red-50';
      case 'warning': return 'text-yellow-600 bg-yellow-50';
      case 'info': return 'text-blue-600 bg-blue-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
      case 'error':
        return <XCircle className="w-4 h-4" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4" />;
      case 'info':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Ahora';
    if (minutes < 60) return `Hace ${minutes}m`;
    if (hours < 24) return `Hace ${hours}h`;
    return `Hace ${days}d`;
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-8 text-center">
        <Shield className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 mb-2">Acceso Restringido</h2>
        <p className="text-gray-600">Solo los superadministradores pueden acceder al panel de monitoreo.</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Cargando datos de monitoreo...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Panel de Monitoreo</h1>
          <p className="text-gray-600">Supervisión de autenticación y seguridad del sistema</p>
        </div>
        <button
          onClick={loadMonitoringData}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <RefreshCw className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <Database className="w-8 h-8 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{recentLogs.length}</p>
          <p className="text-sm text-gray-600">Eventos Recientes</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <AlertTriangle className="w-8 h-8 text-red-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{criticalEvents.length}</p>
          <p className="text-sm text-gray-600">Eventos Críticos</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <Users className="w-8 h-8 text-yellow-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{anomalies.length}</p>
          <p className="text-sm text-gray-600">Anomalías Detectadas</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp className="w-8 h-8 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-gray-900">
            {metricsStats?.enabled ? 'Activo' : 'Inactivo'}
          </p>
          <p className="text-sm text-gray-600">Estado Grafana</p>
        </div>
      </div>

      {metricsStats && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-600" />
            Estado de Métricas
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Métricas en Cola</p>
              <p className="text-xl font-bold text-gray-900">{metricsStats.queuedMetrics}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Logs en Cola</p>
              <p className="text-xl font-bold text-gray-900">{metricsStats.queuedLogs}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Procesando</p>
              <p className="text-xl font-bold text-gray-900">
                {metricsStats.isProcessing ? 'Sí' : 'No'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Grafana</p>
              <p className="text-xl font-bold text-gray-900">
                {metricsStats.enabled ? '✓ Habilitado' : '✗ Deshabilitado'}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={handleTestGrafana}
              disabled={isTestingGrafana || !metricsStats.enabled}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isTestingGrafana ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Probando...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4" />
                  Probar Conexión con Grafana
                </>
              )}
            </button>
            {grafanaTestResult && (
              <p className={`mt-2 text-sm ${grafanaTestResult === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                {grafanaTestResult === 'success' ? '✓ Conexión exitosa' : `✗ ${grafanaTestResult}`}
              </p>
            )}
          </div>
        </div>
      )}

      {anomalies.length > 0 && (
        <div className="bg-red-50 rounded-lg border border-red-200 p-6">
          <h2 className="text-lg font-semibold text-red-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Anomalías Detectadas (Últimas 24h)
          </h2>
          <div className="space-y-3">
            {anomalies.map((anomaly, index) => (
              <div key={index} className="bg-white p-4 rounded-lg border border-red-200">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">{anomaly.user_email}</p>
                    <p className="text-sm text-gray-600">
                      Tipo: {anomaly.anomaly_type.replace('_', ' ')}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-red-600">{anomaly.event_count}</p>
                    <p className="text-xs text-gray-500">eventos</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Última ocurrencia: {formatTimestamp(anomaly.last_occurrence)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {criticalEvents.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-600" />
            Eventos Críticos Recientes
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severidad</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Evento</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contexto</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tiempo</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {criticalEvents.slice(0, 20).map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(event.severity)}`}>
                        {getSeverityIcon(event.severity)}
                        {event.severity}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">{event.event_type}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{event.user_email}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{event.context || '-'}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      <Clock className="w-3 h-3 inline mr-1" />
                      {formatTimestamp(event.logged_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Database className="w-5 h-5 text-blue-600" />
          Registro de Auditoría Reciente
        </h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Severidad</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Evento</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contexto</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Versión</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tiempo</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentLogs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(log.severity)}`}>
                      {getSeverityIcon(log.severity)}
                      {log.severity}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{log.event_type}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{log.context || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{log.app_version || '-'}</td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {formatTimestamp(log.logged_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MonitoringDashboard;
